// od_tour.js — Motor de onboarding con globos de diálogo secuenciales
import { t } from './od_i18n.js';
//
// Uso:
//   import { startTour } from './od_tour.js';
//   startTour(steps, 'od_tour_org_v1');
//
// Cada step: { target: '#cssSelector', text: 'Mensaje', position: 'top'|'bottom'|'left'|'right' }

const BRICK         = '#bf4516';
const BRICK_GLOW    = 'rgba(191,69,22,0.35)';
const OVERLAY_DARK  = 'rgba(0,0,0,0.72)';
const PAD           = 10;   // px extra alrededor del elemento destacado
const BUBBLE_W      = 288;  // ancho fijo del globo
const GAP           = 18;   // distancia entre highlight y borde del globo

/**
 * startTour(steps, storageKey)
 *
 * Muestra los pasos en orden. La página queda congelada (no se puede
 * interactuar con nada) hasta que el usuario completa todos los pasos.
 * Se guarda en localStorage para no volver a mostrarse.
 */
export function startTour(steps, storageKey) {
  if (!steps?.length) return;
  if (storageKey && localStorage.getItem(storageKey)) return;

  // Marcar como visto inmediatamente → no vuelve a aparecer aunque se cierre el browser
  if (storageKey) localStorage.setItem(storageKey, '1');

  let current = 0;

  // ── Overlay de captura ──────────────────────────────────────────────────────
  // Cubre toda la página con pointer-events:all → congela la UI
  const overlay = document.createElement('div');
  overlay.style.cssText = [
    'position:fixed', 'inset:0', 'z-index:99990',
    'pointer-events:all', 'background:transparent',
  ].join(';');
  document.body.appendChild(overlay);

  // ── Highlight ───────────────────────────────────────────────────────────────
  // box-shadow extendido oscurece todo excepto el área del elemento
  const hl = document.createElement('div');
  hl.style.cssText = [
    'position:fixed', 'z-index:99991', 'pointer-events:none',
    'border-radius:8px',
    'transition:left .22s ease,top .22s ease,width .22s ease,height .22s ease',
  ].join(';');
  document.body.appendChild(hl);

  // ── Globo de diálogo ────────────────────────────────────────────────────────
  const bub = document.createElement('div');
  bub.style.cssText = [
    'position:fixed', `z-index:99992`,
    `background:${BRICK}`, 'color:#fff',
    `width:${BUBBLE_W}px`,
    'padding:16px 18px', 'border-radius:12px',
    'font-family:inherit', 'font-size:14px', 'line-height:1.6',
    'box-shadow:0 8px 32px rgba(0,0,0,0.55)',
    'transition:left .22s ease,top .22s ease',
  ].join(';');
  document.body.appendChild(bub);

  // ── Renderizar paso i ───────────────────────────────────────────────────────
  function show(i) {
    const step   = steps[i];
    const isLast = (i === steps.length - 1);
    const target = document.querySelector(step.target);
    const rect   = target ? target.getBoundingClientRect() : null;
    const valid  = rect && rect.width > 2 && rect.height > 2;

    // Actualizar highlight
    if (valid) {
      Object.assign(hl.style, {
        display:   'block',
        left:      `${rect.left   - PAD}px`,
        top:       `${rect.top    - PAD}px`,
        width:     `${rect.width  + PAD * 2}px`,
        height:    `${rect.height + PAD * 2}px`,
        border:    `2.5px solid ${BRICK}`,
        boxShadow: `0 0 0 9999px ${OVERLAY_DARK}, 0 0 18px 3px ${BRICK_GLOW}`,
      });
    } else {
      // No hay target visible → solo oscurecer la pantalla
      hl.style.display = 'none';
      overlay.style.background = OVERLAY_DARK;
    }

    // Calcular posición del globo
    const vw  = window.innerWidth;
    const vh  = window.innerHeight;
    const pos = step.position || 'bottom';
    const r   = valid ? rect : { left: 0, right: vw, top: 0, bottom: vh, width: vw, height: vh };

    let bLeft, bTop;
    if (pos === 'bottom') {
      bLeft = r.left + r.width  / 2 - BUBBLE_W / 2;
      bTop  = r.bottom + PAD + GAP;
    } else if (pos === 'top') {
      bLeft = r.left + r.width  / 2 - BUBBLE_W / 2;
      bTop  = r.top   - PAD - GAP - 145;
    } else if (pos === 'right') {
      bLeft = r.right  + PAD + GAP;
      bTop  = r.top    + r.height / 2 - 72;
    } else { // left
      bLeft = r.left   - PAD - GAP - BUBBLE_W;
      bTop  = r.top    + r.height / 2 - 72;
    }

    // Clamp para no salirse de pantalla
    bLeft = Math.max(12, Math.min(bLeft, vw - BUBBLE_W - 12));
    bTop  = Math.max(12, Math.min(bTop,  vh - 180));

    Object.assign(bub.style, { left: `${bLeft}px`, top: `${bTop}px` });

    // Contenido del globo
    const stepText = typeof step.text === 'function' ? step.text() : step.text;
    bub.innerHTML = `
      <div style="font-size:10.5px;opacity:.6;font-weight:700;letter-spacing:.08em;
                  text-transform:uppercase;margin-bottom:8px">
        ${t('tour_step_counter', i + 1, steps.length)}
      </div>
      <div style="margin-bottom:16px;font-size:14px">${stepText}</div>
      <div style="display:flex;justify-content:flex-end">
        <button style="
          background:rgba(255,255,255,0.18);
          border:1.5px solid rgba(255,255,255,0.5);
          color:#fff;padding:8px 24px;border-radius:7px;
          cursor:pointer;font-size:13px;font-weight:700;
          letter-spacing:.02em;
        ">${isLast ? t('tour_done') : t('tour_next')}</button>
      </div>`;

    bub.querySelector('button').addEventListener('click', () => {
      if (!isLast) show(i + 1);
      else finish();
    });
  }

  function finish() {
    overlay.remove();
    hl.remove();
    bub.remove();
  }

  show(0);
}
